import React from 'react';
import { Link } from 'react-router-dom';
import PageShell from '../components/PageShell';
import Icon from '../components/Icon';
import Eyebrow from '../components/page/Eyebrow';
import { tie } from '../components/page/typography';
import FeaturedArticle from '../components/blog/FeaturedArticle';
import { articles } from '../content/blog';
import { blogIntro } from '../content/pages';
import { serviceMeta } from '../content/services';
import { SITE } from '../components/Seo';

/**
 * Куди ще піти з блогу. Ті самі питання, з якими приходять по статтю, —
 * про ліцензію, Відомості, порядок відкриття, — уже розібрані на цих
 * сторінках, лише в іншому форматі. Підписи кажуть, що там, а не
 * повторюють назву.
 */
const licensing = serviceMeta.find((m) => m.slug === 'licenzuvannya-medychnoyi-praktyky');

const NEXT = [
  { to: '/pytannya', icon: 'fa-comment-dots', title: 'Часті запитання', text: 'Ліцензія, ЕСОЗ, документи й строки — коротко' },
  { to: '/plan-zapusku-kliniky', icon: 'fa-sitemap', title: 'План запуску клініки', text: 'Шість етапів відкриття закладу в порядку залежностей' },
  ...(licensing
    ? [{ to: `/poslugy/${licensing.slug}`, icon: 'fa-id-card', title: licensing.h1, text: 'Як ми ведемо ліцензування від аудиту до наказу' }]
    : [])
];

const BlogIndex = () => {
  const schema = {
    '@type': 'Blog',
    name: 'Блог Visia Medical Group',
    description: 'Зміни в медичному законодавстві України — розбори для керівників клінік і лікарів-ФОП.',
    inLanguage: 'uk-UA',
    blogPost: articles.map((a) => ({
      '@type': 'BlogPosting',
      headline: a.title,
      datePublished: a.date,
      url: `${SITE}/blog/${a.slug}`
    }))
  };

  return (
    <PageShell
      title="Блог: зміни в медичному законодавстві"
      description="Розбори змін у медичному законодавстві України: ліцензійні умови, вимоги МОЗ, ЕСОЗ. Пояснюємо, що це означає для клінік і лікарів-ФОП на практиці."
      path="/blog"
      banner="/images/page-meeting-6ea410d3.jpg"
      schema={schema}
      crumbs={[{ name: 'Блог' }]}
      heading="Зміни в медичному законодавстві"
      lead="Коротко й по суті про те, що змінилось у вимогах до медичної практики — і що з цим робити керівнику клініки чи лікарю-ФОП."
      cta={{ source: 'blog-index' }}
    >
      {/*
        Раніше під банером ішов абзац-вступ на всю ширину — він переказував
        лід банера іншими словами, — а під ним одна тонка картка-рядок і
        одразу заклик: на телефоні до статті треба було прогорнути цілий
        екран вступу, а на широкому екрані сторінка виглядала порожньою.

        Тепер стаття — перше під банером, великою карткою з ключовими датами
        й розділами. Праворуч — для чого цей блог (той самий вступ, але як
        довідка, а не як перший абзац) і куди піти далі; нижня картка
        тягнеться до низу статті, щоб колонки закінчувалися на одній лінії.

        Дві колонки — лише від xl. На 1024 картка поруч із бічною колонкою
        мала б 26rem, і шкала дат у чотири стовпці рвала дати на два рядки;
        там картка на всю ширину, а довідка й переходи — парою під нею. На
        телефоні порядок той самий: стаття, довідка, переходи.
      */}
      <div className="page-container py-10 lg:py-14">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem] xl:gap-8">
          <ul className="list-none m-0 p-0 grid gap-6">
            {articles.map((a) => (
              <li key={a.slug}>
                <FeaturedArticle article={a} />
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-6 md:grid md:grid-cols-2 xl:flex">
            <section aria-labelledby="pro-blog-title" className="rounded-2xl border border-slate-100 bg-slate-50 p-6 lg:p-7">
              <Eyebrow as="h2" id="pro-blog-title">Про що тут пишемо</Eyebrow>
              <p className="text-[0.95rem] leading-relaxed text-slate-600">{tie(blogIntro)}</p>
            </section>

            <nav aria-labelledby="dali-title" className="flex-1 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
              <Eyebrow as="h2" id="dali-title" className="px-3 pt-2 pb-2">
                Корисно далі
              </Eyebrow>
              <ul className="list-none m-0 p-0">
                {NEXT.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="group flex items-start gap-3 rounded-xl px-3 py-3 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 transition-colors duration-200"
                    >
                      <span
                        className="flex-shrink-0 w-9 h-9 rounded-lg border border-teal-100 bg-teal-50 text-teal-700 flex items-center justify-center"
                        aria-hidden="true"
                      >
                        <Icon name={item.icon} className="text-sm" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold leading-snug text-slate-900 group-hover:text-teal-800 transition-colors duration-200">
                          {tie(item.title)}
                        </span>
                        <span className="mt-0.5 block text-sm leading-snug text-slate-600">{tie(item.text)}</span>
                      </span>
                      <Icon
                        name="fa-arrow-right"
                        className="mt-1 flex-shrink-0 text-xs text-teal-700 transition-transform duration-200 motion-reduce:transition-none group-hover:translate-x-0.5"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default BlogIndex;
